import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { SidebarResponsive } from "components/sidebar/Sidebar";
import PropTypes from "prop-types";
import React from "react";
import { MdNotificationsNone, MdNotificationsOff } from "react-icons/md";
import { IoMdMoon, IoMdSunny } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import routes from "routes";
import { useAuth } from "contexts/AuthContext";
import { usePartner } from "hooks/usePartner";
import { PedidoNotificationContent } from "components/menu/PedidoNotificationContent";
import { Mark } from "components/icons/Mark";
import { usePartnerNewOrderNotifications } from "hooks/usePartnerNewOrderNotifications";
import { usePartnerAdminNotifications } from "hooks/usePartnerAdminNotifications";
import { AdminNotificationContent } from "components/menu/AdminNotificationContent";

const partnerRoutes = routes.filter(
  (r) => r.layout === "/partner" && !r.hidden
);

export default function HeaderLinks(props: { secondary: boolean }) {
  const { secondary } = props;
  const { colorMode, toggleColorMode } = useColorMode();
  const { signOut, user } = useAuth();
  const { partner } = usePartner();
  const navigate = useNavigate();
  const { unreadNewOrders, markAsRead, markAllNewOrdersAsRead } =
    usePartnerNewOrderNotifications();
  const {
    unreadAdminNotifications,
    markAsRead: markAdminNotificationRead,
    markAllAsRead: markAllAdminNotificationsRead,
  } = usePartnerAdminNotifications();

  const totalUnread =
    unreadNewOrders.length + unreadAdminNotifications.length;

  const navbarIcon = useColorModeValue("gray.400", "white");
  const menuBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorBrand = useColorModeValue("brand.700", "brand.400");
  const borderColor = useColorModeValue("#E6ECFA", "rgba(152, 162, 179, 0.28)");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)"
  );
  const emptyStateColor = useColorModeValue("secondaryGray.500", "secondaryGray.400");

  const displayName =
    partner?.name ?? user?.email?.split("@")[0] ?? "Parceiro";

  return (
    <Flex
      w={{ sm: "100%", md: "auto" }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={secondary ? { base: "wrap", md: "nowrap" } : "unset"}
      p="10px"
      borderRadius="30px"
      boxShadow={shadow}
    >
      <SearchBar
        mb={() => {
          if (secondary) {
            return { base: "10px", md: "unset" };
          }
          return "unset";
        }}
        me="10px"
        borderRadius="30px"
        placeholder="Pesquisar…"
      />
      <SidebarResponsive routes={partnerRoutes} />
      <Menu>
        <Box position="relative" display="inline-block" me="10px">
          <MenuButton p="0px">
            <Mark
              mt="6px"
              as={MdNotificationsNone}
              color={navbarIcon}
              w="18px"
              h="18px"
            />
          </MenuButton>
          {totalUnread > 0 && (
            <Badge
              position="absolute"
              top="-2px"
              right="-5px"
              padding="4px"
              borderRadius="full"
              px={1.5}
              minW="12px"
              fontSize="8px"
              colorScheme="red"
              variant="solid"
            >
              {totalUnread > 9 ? "9+" : totalUnread}
            </Badge>
          )}
        </Box>
        <MenuList
          boxShadow={shadow}
          p="20px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          me={{ base: "30px", md: "unset" }}
          minW={
            totalUnread === 0
              ? "unset"
              : { base: "unset", md: "400px", xl: "450px" }
          }
          maxW={totalUnread === 0 ? "300px" : { base: "360px", md: "unset" }}
          maxH="70vh"
          overflowY="auto"
        >
          <Flex w="100%" mb="20px" align="center" minH="24px">
            <Text fontSize="md" fontWeight="600" color={textColor}>
              Notificações
            </Text>
            {totalUnread > 0 && (
              <Text
                fontSize="sm"
                fontWeight="500"
                color={textColorBrand}
                ms="auto"
                cursor="pointer"
                onClick={() => {
                  markAllNewOrdersAsRead();
                  void markAllAdminNotificationsRead();
                }}
              >
                Marcar lidas
              </Text>
            )}
          </Flex>
          <Flex flexDirection="column" gap={0}>
            {totalUnread === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py="48px"
                px="16px"
                borderRadius="12px"
              >
                <Mark
                  as={MdNotificationsOff}
                  color={emptyStateColor}
                  w="40px"
                  h="40px"
                  mb="12px"
                />
                <Text fontSize="sm" fontWeight="600" color={textColor} textAlign="center" mb="6px">
                  Nada por aqui
                </Text>
                <Text fontSize="sm" color={emptyStateColor} textAlign="center" lineHeight="tall">
                  Quando houver notificações, você verá tudo neste painel.
                </Text>
              </Flex>
            ) : (
              <>
                {unreadNewOrders.length > 0 && (
                  <>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={emptyStateColor}
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      mb="10px"
                    >
                      Pedidos novos
                    </Text>
                    {unreadNewOrders.map((task) => {
                      const orderLabel =
                        task.orderId?.slice(0, 8) ?? task.id.slice(0, 8);
                      const place =
                        task.deliveryCity && task.deliveryState
                          ? `${task.deliveryCity}/${task.deliveryState}`
                          : task.deliveryCity ?? null;
                      const subtitleParts = [task.customerName, place].filter(
                        Boolean
                      ) as string[];
                      const subtitle =
                        subtitleParts.length > 0
                          ? subtitleParts.join(" · ")
                          : "Pedido novo aguardando processamento.";
                      return (
                        <MenuItem
                          key={`order-${task.id}`}
                          _hover={{ bg: "none" }}
                          _focus={{ bg: "none" }}
                          px="0"
                          borderRadius="8px"
                          mb="10px"
                          onClick={() => {
                            markAsRead(task.id);
                            navigate(`/partner/orders/${task.id}`);
                          }}
                        >
                          <PedidoNotificationContent
                            orderLabel={orderLabel}
                            subtitle={subtitle}
                          />
                        </MenuItem>
                      );
                    })}
                  </>
                )}
                {unreadAdminNotifications.length > 0 && (
                  <>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={emptyStateColor}
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      mb="10px"
                      mt={unreadNewOrders.length > 0 ? "14px" : "0"}
                    >
                      Avisos
                    </Text>
                    {unreadAdminNotifications.map((n) => {
                      const subtitle =
                        n.body?.trim().length > 0
                          ? n.body.trim()
                          : "Mensagem da equipe OnlyWine.";
                      return (
                        <MenuItem
                          key={`admin-${n.id}`}
                          _hover={{ bg: "none" }}
                          _focus={{ bg: "none" }}
                          px="0"
                          borderRadius="8px"
                          mb="10px"
                          onClick={() => {
                            void markAdminNotificationRead(n.id);
                          }}
                        >
                          <AdminNotificationContent
                            title={n.title || "Aviso"}
                            subtitle={subtitle}
                          />
                        </MenuItem>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Flex>
        </MenuList>
      </Menu>

      <Button
        variant="no-hover"
        bg="transparent"
        p="0px"
        minW="unset"
        minH="unset"
        h="18px"
        w="max-content"
        onClick={toggleColorMode}
      >
        <Mark
          me="10px"
          h="18px"
          w="18px"
          color={navbarIcon}
          as={colorMode === "light" ? IoMdMoon : IoMdSunny}
        />
      </Button>
      <Menu>
        <MenuButton p="0px">
          <Avatar
            _hover={{ cursor: "pointer" }}
            color="white"
            name={displayName}
            bg="brand.500"
            size="sm"
            w="40px"
            h="40px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
            >
              Olá, {displayName}
            </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem
              _hover={{ bg: "none" }}
              _focus={{ bg: "none" }}
              borderRadius="8px"
              px="14px"
              onClick={() => navigate("/partner/profile")}
            >
              <Text fontSize="sm">Perfil</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: "none" }}
              _focus={{ bg: "none" }}
              color="red.400"
              borderRadius="8px"
              px="14px"
              onClick={() => signOut()}
            >
              <Text fontSize="sm">Sair</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
